import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getColorSchemesForExam, getPearsonColorSchemeDropdownLabel } from '../constants/tmuaColorSchemes';
import { TMUA_PEARSON_TOKENS, getTmuaPearsonChrome } from '../constants/tmuaPearsonTokens';
import { resolveTmuaPearsonAccessKey } from '../examKeyboardPolicy';
import { useVisualViewportKeyboardInset } from '../hooks/useVisualViewportKeyboardInset';
import { TmuaPearsonKeyboardInsetContext } from '../hooks/TmuaPearsonKeyboardInsetContext';
import { BMAT_MODULE_LABELS, BMAT_PEARSON_TITLE } from '../utils/classifyBmatPaperSection';

export const TMUA_PEARSON_TITLE = 'Test of Mathematics for University Admission';
export const LNAT_PEARSON_TITLE = 'National Admissions Test for Law (LNAT)';
export const LSAT_PEARSON_TITLE = 'Law School Admission Test (LSAT)';
export const ESAT_PEARSON_TITLE = 'Engineering and Science Admissions Test (ESAT)';
export const NSAA_PEARSON_TITLE = 'Natural Sciences Admissions Assessment (NSAA)';
export const ENGAA_PEARSON_TITLE = 'Engineering Admissions Assessment (ENGAA)';
export const TARA_PEARSON_TITLE = 'Test of Academic Reasoning for Admissions (TARA)';
export const TSA_PEARSON_TITLE = 'Thinking Skills Assessment (TSA)';
export const UCAT_PEARSON_TITLE = 'University Clinical Aptitude Test (UCAT)';

const TARA_MODULE_TITLES = {
  critical_thinking: 'Critical Thinking',
  problem_solving: 'Problem Solving',
  writing: 'Writing Task',
};

export function getPearsonPlayerExamTitle(examName, moduleKey, options = {}) {
  if (options.tsaSitting) return TSA_PEARSON_TITLE;
  const bmat = Boolean(options.bmatSitting || options.heritageExam === 'BMAT');
  if (bmat) {
    if (moduleKey && BMAT_MODULE_LABELS[moduleKey]) return BMAT_MODULE_LABELS[moduleKey];
    return BMAT_PEARSON_TITLE;
  }
  const upper = String(examName || '').toUpperCase();
  if (options.lsatPractice) return LSAT_PEARSON_TITLE;
  if (upper === 'LNAT') return LNAT_PEARSON_TITLE;
  const heritage = String(options.heritageExam || '').toUpperCase();
  if (heritage === 'NSAA') return NSAA_PEARSON_TITLE;
  if (heritage === 'ENGAA') return ENGAA_PEARSON_TITLE;
  if (upper === 'ESAT') return ESAT_PEARSON_TITLE;
  if (upper === 'TARA') {
    if (moduleKey && TARA_MODULE_TITLES[moduleKey]) return TARA_MODULE_TITLES[moduleKey];
    return TARA_PEARSON_TITLE;
  }
  if (upper === 'UCAT') return UCAT_PEARSON_TITLE;
  return TMUA_PEARSON_TITLE;
}

export function isLnatPearsonExam(examName) {
  return String(examName || '').toUpperCase() === 'LNAT';
}

export function isEsatPearsonExam(examName) {
  return String(examName || '').toUpperCase() === 'ESAT';
}

export function isTaraPearsonExam(examName) {
  return String(examName || '').toUpperCase() === 'TARA';
}

export function isUcatPearsonExam(examName) {
  return String(examName || '').toUpperCase() === 'UCAT';
}

export function useTmuaPearsonAccessKeys(snapshot, handlers) {
  const snapshotRef = useRef(snapshot);
  const handlersRef = useRef(handlers);
  snapshotRef.current = snapshot;
  handlersRef.current = handlers;

  useEffect(() => {
    const onKeyDown = (event) => {
      const snap = snapshotRef.current || {};
      const fns = handlersRef.current || {};
      const decision = resolveTmuaPearsonAccessKey(event, {
        isTmuaPearsonPlayer: true,
        ...snap,
      });
      if (decision.type === 'ignore') return;
      if (decision.preventDefault) event.preventDefault();
      if (decision.type === 'next' && fns.onNext) fns.onNext();
      if (decision.type === 'endExam' && fns.onEndExam) fns.onEndExam();
      if (decision.type === 'confirmYes' && fns.onConfirmYes) fns.onConfirmYes();
      if (decision.type === 'confirmNo' && fns.onConfirmNo) fns.onConfirmNo();
      if (decision.type === 'dismissOk' && fns.onDismissOk) fns.onDismissOk();
      if (decision.type === 'instructions' && fns.onInstructions) fns.onInstructions();
      if (decision.type === 'highlight' && fns.onHighlight) fns.onHighlight();
      if (decision.type === 'strikethrough' && fns.onStrikethrough) fns.onStrikethrough();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}

const DoorIcon = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16,17 21,12 16,7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const NextIcon = ({ color }) => (
  <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true">
    <polygon points="1.5,1 11,6 1.5,11" fill={color} />
  </svg>
);

const PrevIcon = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="11,18 5,12 11,6" />
  </svg>
);

const NavigatorIcon = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <circle cx="6" cy="6" r="2.1" />
    <circle cx="18" cy="6" r="2.1" />
    <circle cx="6" cy="18" r="2.1" />
    <circle cx="18" cy="18" r="2.1" />
    <circle cx="12" cy="12" r="2.1" />
  </svg>
);

const ExamListIcon = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" strokeWidth="3.5" />
    <line x1="3" y1="12" x2="3.01" y2="12" strokeWidth="3.5" />
    <line x1="3" y1="18" x2="3.01" y2="18" strokeWidth="3.5" />
  </svg>
);

const MoreMenuIcon = ({ color = '#000' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <circle cx="5" cy="12" r="2.2" />
    <circle cx="12" cy="12" r="2.2" />
    <circle cx="19" cy="12" r="2.2" />
  </svg>
);

const HelpIcon = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1.5 1-1.5 2.2" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const RetakeIcon = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 12a9 9 0 1 1 3 6.7" />
    <polyline points="3,17 3,12 8,12" />
  </svg>
);

const ReviewIcon = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="14" y2="18" />
  </svg>
);

export function TmuaPearsonFooterButton({
  children,
  onClick,
  variant = 'chrome',
  icon,
  iconPosition = 'start',
  underline,
  keepLabelOnMobile = false,
}) {
  const isYellow = variant === 'yellow';
  const iconColor = 'currentColor';
  const iconNode = React.isValidElement(icon)
    ? React.cloneElement(icon, { color: iconColor })
    : icon;
  const labelText = typeof children === 'string' && underline
    ? children.split('').map((ch, i) => (ch.toLowerCase() === underline ? <u key={i}>{ch}</u> : ch))
    : children;
  const label = (
    <span className={keepLabelOnMobile ? undefined : 'tmua-pearson-footer-label'}>
      {labelText}
    </span>
  );
  const wrappedIcon = iconNode ? (
    <span className="tmua-pearson-footer-icon">{iconNode}</span>
  ) : null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={isYellow ? 'tmua-pearson-btn-yellow' : 'tmua-pearson-btn-chrome'}
      aria-label={typeof children === 'string' ? children : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        flexDirection: iconPosition === 'end' ? 'row-reverse' : 'row',
        gap: 5,
        height: '100%',
        padding: '0 12px',
        fontFamily: 'Tahoma, Arial, Helvetica, sans-serif',
        fontSize: 16,
        fontWeight: 700,
        color: 'inherit',
        background: 'transparent',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        borderRadius: 0,
      }}
    >
      {wrappedIcon}
      {label}
    </button>
  );
}

export default function TmuaPearsonShell({
  colorScheme = 'pearsonDefault',
  setColorScheme,
  examName = 'TMUA',
  title,
  tsaSitting = false,
  heritageExam,
  colorSchemeLabel,
  toolbarLeft = null,
  toolbarRight = null,
  counter = null,
  timeRemainingLabel = null,
  footerLeft = null,
  footerRight = null,
  onBackToSelection = null,
  children,
  containerRef,
  onWheel,
}) {
  const T = TMUA_PEARSON_TOKENS;
  const chrome = getTmuaPearsonChrome(colorScheme);
  const schemes = getColorSchemesForExam(examName || 'TMUA', { pearsonPlayer: true });
  const displayTitle = title || getPearsonPlayerExamTitle(examName, undefined, { tsaSitting, heritageExam });
  const schemeDropdownLabel = colorSchemeLabel || getPearsonColorSchemeDropdownLabel(examName);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const [isMobileWidth, setIsMobileWidth] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = () => setIsMobileWidth(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const keyboardInset = useVisualViewportKeyboardInset();
  // ignore small viewport jitter that isn't a real keyboard, and never treat
  // this as "open" above 768px even if visualViewport reports a delta
  const keyboardOpen = isMobileWidth && keyboardInset > 80;
  const scrollAnchorRef = useRef(null);
  const scrollDirectionAccumRef = useRef(0);

  const handleBodyScrollCapture = useCallback((event) => {
    // Auto-hide is mobile-only chrome behavior; never let it engage on
    // desktop widths, since the mobile header itself is display:none there
    // and the pinned timer pill has no CSS gate of its own.
    if (typeof window === 'undefined' || !window.matchMedia('(max-width: 768px)').matches) return;
    const target = event.target;
    if (!target || typeof target.scrollTop !== 'number') return;
    const previous = scrollAnchorRef.current;
    scrollAnchorRef.current = target;
    if (previous !== target) {
      // Switched to a different scrolling descendant (e.g. passage vs
      // question pane in split view) — restart the accumulator rather than
      // carrying over a delta measured against a different element.
      target.dataset.tmuaPearsonLastScrollTop = String(target.scrollTop);
      return;
    }
    const lastScrollTop = Number(target.dataset.tmuaPearsonLastScrollTop || 0);
    const delta = target.scrollTop - lastScrollTop;
    target.dataset.tmuaPearsonLastScrollTop = String(target.scrollTop);

    if (delta > 0) {
      scrollDirectionAccumRef.current = Math.max(0, scrollDirectionAccumRef.current) + delta;
      if (scrollDirectionAccumRef.current >= 40) {
        setHeaderHidden(true);
      }
    } else if (delta < 0) {
      scrollDirectionAccumRef.current = Math.min(0, scrollDirectionAccumRef.current) + delta;
      if (scrollDirectionAccumRef.current <= -10) {
        setHeaderHidden(false);
        scrollDirectionAccumRef.current = 0;
      }
    }
  }, []);

  const handleHeaderTap = useCallback(() => {
    if (headerHidden) {
      setHeaderHidden(false);
      scrollDirectionAccumRef.current = 0;
    }
  }, [headerHidden]);

  const isLnat = isLnatPearsonExam(examName);

  return (
    <div
      ref={containerRef}
      className={isLnat ? 'tmua-pearson-shell tmua-pearson-lnat' : 'tmua-pearson-shell'}
      tabIndex={-1}
      onWheel={onWheel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10100,
        display: 'flex',
        flexDirection: 'column',
        background: chrome.body,
        color: chrome.text,
        fontFamily: T.fontUi,
        outline: 'none',
      }}
    >
      <style>{`
        .product-back-btn,
        .quiz-unified-back-btn {
          display: none !important;
        }
        button.tmua-pearson-btn-yellow,
        button.tmua-pearson-btn-chrome {
          background: transparent !important;
          background-color: transparent !important;
          color: inherit !important;
          box-shadow: none !important;
        }
        .tmua-pearson-footer-left > button {
          border-right: 1px solid ${T.titleBarFg} !important;
        }
        .tmua-pearson-footer-right > button {
          border-left: 1px solid ${T.titleBarFg} !important;
        }
        select.tmua-pearson-color-scheme {
          -webkit-appearance: none;
          appearance: none;
          width: 190px;
          height: 100%;
          margin: 0;
          padding: 0 22px 0 8px;
          border: 1px solid #000;
          border-radius: 0;
          background-color: ${chrome.toolbar};
          color: #000;
          font-family: Tahoma, Arial, Helvetica, sans-serif;
          font-size: 13px;
          font-weight: 400;
          line-height: 1;
          cursor: pointer;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='8' viewBox='0 0 10 8'><polygon points='0,0 10,0 5,8' fill='%23000'/></svg>");
          background-repeat: no-repeat;
          background-position: right 7px center;
          background-size: 9px 7px;
        }
        select.tmua-pearson-color-scheme:focus {
          outline: 1px solid #000;
          outline-offset: 0;
        }
        .tmua-pearson-body,
        .tmua-pearson-body p,
        .tmua-pearson-body li,
        .tmua-pearson-body span,
        .tmua-pearson-body div,
        .tmua-pearson-instruction-copy,
        .tmua-pearson-instruction-copy p,
        .tmua-pearson-instruction-copy li,
        .tmua-pearson-instruction-copy span,
        .tmua-pearson-instruction-copy div {
          color: #000000 !important;
        }
        .question-text,
        .question-text *,
        .mathjax-process,
        .mathjax-process *,
        mjx-container,
        .MathJax {
          color: #000000 !important;
        }
        .tmua-pearson-body .question-text,
        .tmua-pearson-body .question-text p {
          text-align: left !important;
        }
        /* LNAT Pearson item type is 14px Tahoma (12 + 2). First passage <p> is the
           centred title (Faith and the Theologian / Linguistics). */
        .tmua-pearson-lnat .tmua-pearson-body .question-text,
        .tmua-pearson-lnat .tmua-pearson-body .question-text p,
        .tmua-pearson-lnat .tmua-pearson-body .question-text li,
        .tmua-pearson-lnat .tmua-pearson-body .question-text span,
        .tmua-pearson-lnat .tmua-pearson-body .question-text strong,
        .tmua-pearson-lnat .tmua-pearson-body .question-text em,
        .tmua-pearson-lnat .tmua-pearson-body .question-text div,
        .tmua-pearson-lnat .tmua-pearson-body .mathjax-process,
        .tmua-pearson-lnat .tmua-pearson-body .exhibit-clickable-html {
          font-size: 14px !important;
          line-height: 1.25 !important;
          font-family: Tahoma, Arial, Helvetica, sans-serif !important;
        }
        .tmua-pearson-lnat .tmua-pearson-body .lnat-pearson-essay-prompt,
        .tmua-pearson-lnat .tmua-pearson-body .lnat-pearson-essay-prompt * {
          font-size: 17px !important;
        }
        .tmua-pearson-lnat .lnat-pearson-hl {
          user-select: text;
        }
        .tmua-pearson-lnat .lnat-pearson-strike {
          text-decoration: line-through;
          text-decoration-thickness: 1.5px;
          text-decoration-color: #333333;
          user-select: text;
        }
        .tmua-pearson-lnat .tmua-pearson-body .question-text p {
          margin: 0 0 8px !important;
        }
        .tmua-pearson-lnat .tmua-pearson-body .question-text p:last-child {
          margin-bottom: 0 !important;
        }
        .tmua-pearson-lnat .ivori-exam-passage-pane .question-text > p:first-child {
          text-align: center !important;
          font-weight: 700;
          margin: 0 0 14px !important;
        }
        .tmua-pearson-lnat .ivori-exam-passage-pane .question-text > p:has(> br:only-child) {
          margin: 0 0 4px !important;
        }
        .tmua-pearson-body mjx-container[display="true"] {
          display: block !important;
          text-align: left !important;
          margin: 0.55em 0 0.55em 2.75em !important;
        }
        .tmua-pearson-body img {
          max-width: min(100%, 22rem) !important;
          max-height: min(36vh, 16rem) !important;
          width: auto !important;
          height: auto !important;
          object-fit: contain;
          display: block;
          margin: 12px 0;
        }
        .tmua-pearson-desktop-titlebar,
        .tmua-pearson-desktop-toolbar {
          display: flex;
        }
        .tmua-pearson-mobile-header {
          display: none;
        }
        .tmua-pearson-footer-icon {
          display: none;
        }
        .tmua-pearson-footer-label {
          display: inline;
        }
        @media (max-width: 768px) {
          .tmua-pearson-desktop-titlebar,
          .tmua-pearson-desktop-toolbar {
            display: none !important;
          }
          .tmua-pearson-mobile-header {
            display: flex !important;
            transform: translateY(${headerHidden ? '-100%' : '0'});
            transition: transform 150ms ease-out;
          }
          .tmua-pearson-footer {
            height: ${T.footerHeightMobile}px !important;
          }
          .tmua-pearson-footer-icon {
            display: inline-flex !important;
          }
          .tmua-pearson-footer-label {
            display: none !important;
          }
        }
      `}</style>

      {/* Desktop title bar + toolbar — unchanged from pre-mobile behavior, hidden below 768px */}
      <div
        className="tmua-pearson-desktop-titlebar"
        style={{
          flex: 'none',
          height: T.titleBarHeight,
          background: chrome.titleBar,
          color: chrome.chromeFg,
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 10px',
          fontSize: 20,
          fontWeight: 400,
          fontFamily: T.fontUi,
        }}
      >
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayTitle}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {timeRemainingLabel ? (
            <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 13, fontWeight: 600 }}>
              Time Remaining {timeRemainingLabel}
            </span>
          ) : null}
          {counter ? (
            <button
              type="button"
              onClick={counter.onToggle}
              title={counter.visible ? 'Hide Screen Counter' : 'Show Screen Counter'}
              style={{
                background: 'transparent',
                border: 'none',
                color: chrome.chromeFg,
                cursor: 'pointer',
                fontFamily: T.fontUi,
                fontSize: 13,
                fontWeight: 600,
                padding: 0,
              }}
            >
              {counter.visible ? `${counter.current} of ${counter.total}` : ''}
              <span style={{ marginLeft: counter.visible ? 6 : 0, opacity: 0.9 }}>▦</span>
            </button>
          ) : null}
        </div>
      </div>

      <div
        className="tmua-pearson-desktop-toolbar"
        style={{
          flex: 'none',
          height: T.toolbarHeight,
          background: chrome.toolbar,
          color: chrome.chromeFg,
          alignItems: 'stretch',
          justifyContent: 'space-between',
          padding: '0 0 0 8px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingLeft: 2 }}>{toolbarLeft}</div>
        <div style={{ display: 'flex', alignItems: 'stretch', height: '100%', marginLeft: 'auto' }}>
          {toolbarRight}
          {setColorScheme ? (
            <select
              className="tmua-pearson-color-scheme"
              value={colorScheme}
              onChange={(e) => setColorScheme(e.target.value)}
              aria-label={schemeDropdownLabel}
            >
              {Object.entries(schemes).map(([key, scheme]) => (
                <option key={key} value={key}>
                  {key === 'pearsonDefault' ? schemeDropdownLabel : scheme.label}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      </div>

      {/* Mobile-only 44px header (<768px): ⋯ menu, timer, counter (opens navigator). Hidden while the keyboard is open — replaced by the 36px minimal bar below. */}
      {!keyboardOpen ? (
        <div
          className="tmua-pearson-mobile-header"
          onClick={handleHeaderTap}
          style={{
            flex: 'none',
            height: 44,
            background: chrome.titleBar,
            color: chrome.chromeFg,
            alignItems: 'center',
            justifyContent: 'space-between',
            fontFamily: T.fontUi,
            position: 'relative',
            zIndex: 3,
          }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMobileMenuOpen((v) => !v); }}
            aria-label="More options"
            style={{
              width: 44,
              height: 44,
              flex: 'none',
              background: 'transparent',
              border: 'none',
              color: chrome.chromeFg,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MoreMenuIcon color={chrome.chromeFg} />
          </button>
          <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 15, fontWeight: 700 }}>
            {timeRemainingLabel || ''}
          </span>
          {counter ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); counter.onOpenNavigator && counter.onOpenNavigator(); }}
              style={{
                minWidth: 60,
                height: 44,
                flex: 'none',
                background: 'transparent',
                border: 'none',
                color: chrome.chromeFg,
                cursor: 'pointer',
                fontFamily: T.fontUi,
                fontSize: 13,
                fontWeight: 600,
                padding: '0 10px',
              }}
            >
              {counter.current} of {counter.total}
            </button>
          ) : <span style={{ width: 44 }} />}
        </div>
      ) : (
        <div
          className="tmua-pearson-keyboard-open-bar"
          style={{
            display: 'flex',
            flex: 'none',
            height: 36,
            background: chrome.titleBar,
            color: chrome.chromeFg,
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: T.fontUi,
          }}
        >
          <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 13, fontWeight: 700 }}>
            {timeRemainingLabel || ''}
          </span>
        </div>
      )}

      {/* Pinned timer pill — the timer must never be fully hidden, even while the mobile header auto-hides. Only rendered while the header is actually hidden, so it never competes with the header's own timer for space. */}
      {headerHidden ? (
        <div
          className="tmua-pearson-pinned-timer"
          style={{
            display: 'flex',
            position: 'absolute',
            top: 4,
            right: 8,
            zIndex: 4,
            height: 20,
            padding: '0 8px',
            borderRadius: 10,
            background: chrome.titleBar,
            color: chrome.chromeFg,
            alignItems: 'center',
            justifyContent: 'center',
            fontVariantNumeric: 'tabular-nums',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {timeRemainingLabel}
        </div>
      ) : null}

      {/* ⋯ menu — relocates toolbarLeft/toolbarRight/colour-scheme select on mobile, no call-site changes needed */}
      {mobileMenuOpen ? (
        <>
          <div
            onClick={() => setMobileMenuOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 10199, background: 'rgba(0,0,0,0.25)' }}
          />
          <div
            style={{
              position: 'absolute',
              top: 44,
              left: 0,
              zIndex: 10200,
              minWidth: 220,
              maxWidth: '80vw',
              background: chrome.toolbar,
              border: `1px solid ${T.titleBarFg}`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: 'column',
              padding: 8,
              gap: 4,
              overflow: 'visible',
            }}
          >
            {setColorScheme ? (
              <select
                className="tmua-pearson-color-scheme"
                value={colorScheme}
                onChange={(e) => setColorScheme(e.target.value)}
                aria-label={schemeDropdownLabel}
                style={{ width: '100%', height: 40 }}
              >
                {Object.entries(schemes).map(([key, scheme]) => (
                  <option key={key} value={key}>
                    {key === 'pearsonDefault' ? schemeDropdownLabel : scheme.label}
                  </option>
                ))}
              </select>
            ) : null}
            {toolbarLeft}
            {toolbarRight}
          </div>
        </>
      ) : null}

      <div
        className="tmua-pearson-body"
        onScrollCapture={handleBodyScrollCapture}
        style={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          background: chrome.body,
          color: '#000000',
        }}
      >
        <TmuaPearsonKeyboardInsetContext.Provider value={keyboardOpen ? keyboardInset : 0}>
          {children}
        </TmuaPearsonKeyboardInsetContext.Provider>
      </div>

      {!keyboardOpen ? (
        <div
          className="tmua-pearson-footer"
          style={{
            flex: 'none',
            height: T.footerHeight,
            background: chrome.footer,
            color: chrome.chromeFg,
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'space-between',
            padding: 0,
            zIndex: 2,
          }}
        >
          <div className="tmua-pearson-footer-left" style={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
            {footerLeft}
            {onBackToSelection ? (
              <TmuaPearsonFooterButton onClick={onBackToSelection} icon={<ExamListIcon color={chrome.chromeFg} />}>
                Back to Exam List
              </TmuaPearsonFooterButton>
            ) : null}
          </div>
          <div className="tmua-pearson-footer-right" style={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
            {footerRight}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { DoorIcon, NextIcon, PrevIcon, NavigatorIcon, ExamListIcon, HelpIcon, RetakeIcon, ReviewIcon };
